import axios from 'axios'
import api, { getApiBaseUrl, isApiBaseUrlLocked, setApiBaseUrl } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import { requestFcmToken } from '@/lib/firebase'
import { getBookmarkedPostKey, useAuthStore, User, Post, Branch } from '@/stores/auth-store'
import { buildLoginRedirectPath } from '@/lib/auth-redirect'
import { isOrganizationServiceVariant } from '@/lib/service-variant-types'
import type { PaginatedResponse } from '@/types'

const DEFAULT_SERVICE_INSTANCE_ID = import.meta.env.VITE_JOB_ORG_INSTANCE_ID || 'uu5jVVTB746426641964'
const DEFAULT_PLATFORM = Number(import.meta.env.VITE_JOB_PLATFORM || 1)
const DEFAULT_DEVICE_ID = import.meta.env.VITE_JOB_DEVICE_ID || 'frontend-web-001'
const DEFAULT_USER_GROUP_UNIQUE = 'USER'
const POSTS_LIST_CACHE_TTL_MS = 30 * 60 * 1000

const hasCapabilityLikePayload = (value: unknown) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.values(value as Record<string, unknown>).some((entry) => {
    if (entry === null || entry === undefined) {
      return false
    }
    const t = typeof entry
    return t === 'boolean' || t === 'number' || t === 'string'
  })
}

// Types matching the API spec
export interface LoginPayload {
  type?: 'email' | 'phn_nbr' | 'username'
  email?: string
  username?: string
  phn_nbr?: string
  password: string
  tenant?: string
  company_identifier?: string
  service_instance_id?: string
  org_instance_id?: string
  platform?: number
  device_id?: string
  user_group_unique?: string
  fcm_token?: string
}

export interface SignupPayload {
  signup_type: 'company' | 'user' | 'vendor'
  company_name?: string
  identifier?: string
  facility_product?: string
  owner_name?: string
  name?: string
  tenant?: string
  first_name?: string
  last_name?: string
  email?: string
  country_id?: number
  state_id?: number
  phone_number?: string
  date_of_birth?: string
  age?: string
  gender?: string
  password: string
  company_identifier?: string
  service_instance_id?: string
  // Vendor signup fields
  vendor_name?: string
  branch_name?: string
  service_variant_id?: string | number
  org_instance_id?: string
  parent_org_instance_id?: string
  parent_org_identifier?: string
  accept_user_policy?: boolean
  acknowledge_exposure_notice?: boolean
  user_group_unique?: string
}

export interface ForgotPasswordPayload {
  org_instance_id?: string
  email?: string
  country_id?: number
  phn_nbr?: string
  user_group_unique?: string
}

export interface ForgotPasswordResponseData {
  context?: string
  message?: string
  otp_length?: number
}

export interface ResetPasswordPayload {
  token: string
  new_password: string
  confirm_password: string
  user_group_unique?: string
}

export interface ChangePasswordPayload {
  old_pwd: string
  new_pwd: string
}

export interface VerifySignupEmailOtpPayload {
  email: string
  code: string
  user_group_unique?: string
}

export interface VerifyForgotPasswordEmailOtpPayload {
  email: string
  code: string
  user_group_unique?: string
}

export interface VerifyForgotPasswordPhoneOtpPayload {
  org_instance_id: string
  country_id: number
  phn_nbr: string
  code: string
  user_group_unique?: string
}

export interface ResendSignupVerificationEmailPayload {
  email: string
  org_instance_id: string
}

export interface LoginResponse {
  status?: string
  message: string
  more_info?: Record<string, unknown>
  data: {
    access_token?: string
    refresh_token?: string
    session_id?: string
    service_instance_id?: string
    company_identifier?: string
    context?: string
    message?: string
    otp_length?: number
    session_data?: {
      platform: number
      device_id: string
    }
    user?: {
      id: number | string
      username: string
      fname: string | null
      lname: string | null
      email: string
      status?: string | number | null
      registration_status?: string | number | null
    }
    posts?: Post[]
    billing?: {
      subscription_status: 'trial' | 'active' | 'expired' | null
      trial_end_on?: string | null
      is_locked: boolean
    } | null
  }
}

export interface SignupResponse {
  status: string
  message: string
  more_info: Record<string, unknown>
  data: {
    id: number
    org_instance_id?: string
    service_instance_id?: string
    company_identifier?: string
    first_name?: string
    last_name?: string
    name?: string
    company_name?: string
    email: string
    context?: string
    message?: string
    otp_length?: number
    reactivated_act?: boolean
  }
}

export interface SecurityCompanySignupPayload {
  company_name: string
  owner_name: string
  email: string
  password: string
  country_id: string
  phone_number: string
  city: string
  branch_name?: string
  plan?: 'basic' | 'premium'
}

export interface SecurityCompanySignupResponse {
  status: string
  message: string
  more_info: Record<string, unknown>
  data: {
    company_name: string
    identifier: string
    org_instance_id: string
    tenant_id: number
    branch_instance_id: string
    security_instance_id: string
    security_service_instance_pk: number
    worker_id: number
    post_id: number
    email: string
    email_sent: boolean
    email_message: string
    phone_number: string
    city: string
    plan: string
    login_hints: {
      email: string
      phone_number: string
      company_identifier: string
      org_instance_id: string
    }
    steps: { id: string; status: string }[]
  }
}

export interface ApiResponse<T = unknown> {
  status: string
  message: string
  more_info: Record<string, unknown>
  data: T
}

interface AuthContextResponse {
  status?: string
  message?: string
  more_info?: Record<string, unknown>
  data?: {
    posts?: Post[]
    session_id?: string
  }
}

type PostsListResponse = ApiResponse<Post[] | PaginatedResponse<Post>> | PaginatedResponse<Post> | Post[]
type PostsListResult = { posts: Post[]; message: string | null }
type PostsListFetchOptions = {
  includeCapabilities?: boolean
  postId?: string | number | null
  forceRefresh?: boolean
}

const USER_POSTLIST_FALLBACK_BASE_URLS = [
  'http://127.0.0.1:8002',
  'http://localhost:8002',
  'http://127.0.0.1:8000',
  'http://localhost:8000',
  'http://127.0.0.1:8001',
  'http://localhost:8001',
]

const isLocalBackendBaseUrl = (value: string) => (
  /^(https?:\/\/)?(localhost|127\.0\.0\.1|\[::1\])(:\d+)?\/?$/i.test(String(value || '').trim())
)

type ServiceInstanceRecord = Record<string, unknown>

const getRecordValue = (record: ServiceInstanceRecord, ...keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return undefined
}

const normalizeBranch = (record: ServiceInstanceRecord): Branch => ({
  id: getRecordValue(record, 'id', '_id', 'instance_id', 'instance-id') as string | number,
  name: String(getRecordValue(record, 'name', 'title', 'instance_id', 'instance-id') ?? 'Unnamed branch'),
  code: getRecordValue(record, 'identifier', 'code', 'instance_id', 'instance-id') as string | undefined,
  instanceId: getRecordValue(record, 'instance_id', 'instance-id', 'code') as string | undefined,
  identifier: getRecordValue(record, 'identifier', 'code') as string | undefined,
  currencySymbol: getRecordValue(record, 'currency_symbol') as string | undefined,
  currencyCode: getRecordValue(record, 'currency_code') as string | undefined,
  status: getRecordValue(record, 'status') as string | number | undefined,
})

const normalizeBranches = (
  payload?: ApiResponse<ServiceInstanceRecord[] | PaginatedResponse<ServiceInstanceRecord>>
    | PaginatedResponse<ServiceInstanceRecord>
    | ServiceInstanceRecord[]
): Branch[] => {
  if (!payload) {
    return []
  }

  const data = 'data' in payload ? payload.data : payload
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data.results)
      ? data.results
      : []

  return rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => normalizeBranch(row))
}

const POSTS_LIST_PAGE_SIZE = 200

const shouldDebugUserPostList = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const query = new URLSearchParams(window.location.search)
  if (query.get('debug_user_posts') === '1') {
    return true
  }
  return window.localStorage.getItem('debug_user_posts') === '1'
}

const asTrimmedText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value).trim()
}

const normalizePostRow = (row: Post): Post | null => {
  const record = row as unknown as Record<string, unknown>
  const rolesData = (record.roles_data && typeof record.roles_data === 'object')
    ? (record.roles_data as Record<string, unknown>)
    : null
  const userData = (record.user_data && typeof record.user_data === 'object')
    ? (record.user_data as Record<string, unknown>)
    : null
  const userDataMeta = (userData?.data && typeof userData.data === 'object')
    ? (userData.data as Record<string, unknown>)
    : null

  const normalizedId = record.id ?? record._id
  if (normalizedId === null || normalizedId === undefined || asTrimmedText(normalizedId) === '') {
    return null
  }

  const normalizedRoleName =
    asTrimmedText(record.role_name)
    || asTrimmedText(record.roles_name)
    || asTrimmedText(rolesData?.roles_name)

  const normalizedRoleId =
    record.role_id
    ?? rolesData?.id
    ?? rolesData?.unique_id

  const normalizedServiceInstance =
    record.service_instance
    ?? record.service_instance_id
    ?? userDataMeta?.instance_id

  const normalizedServiceVariantName =
    asTrimmedText(record.service_variant_name)
    || asTrimmedText(record.service_variant_type)
    || asTrimmedText(userDataMeta?.service_variant_name)

  return {
    ...row,
    id: normalizedId as Post['id'],
    role_name: normalizedRoleName,
    role_id: (normalizedRoleId as Post['role_id']) ?? row.role_id,
    service_instance: (normalizedServiceInstance as Post['service_instance']) ?? row.service_instance,
    service_variant_name: normalizedServiceVariantName || row.service_variant_name,
  }
}

const extractPostsRows = (payload?: PostsListResponse): Post[] => {
  if (!payload) {
    return []
  }

  const data = 'data' in payload ? payload.data : payload
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : []

  return rows
    .filter((row): row is Post => Boolean(row && typeof row === 'object'))
    .map((row) => normalizePostRow(row))
    .filter((row): row is Post => Boolean(row))
}

const filterPostsForCurrentUser = (posts: Post[]): Post[] => {
  const authState = useAuthStore.getState()
  const user = authState.user
  if (!user || posts.length === 0) {
    return posts
  }

  const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase()
  const userFirst = normalize(user.first_name)
  const userLast = normalize(user.last_name)
  const userFull = normalize([user.first_name, user.last_name].filter(Boolean).join(' '))
  const userUsername = normalize(user.username)
  const userEmail = normalize(user.email)
  const candidates = new Set([userFirst, userLast, userFull, userUsername, userEmail].filter(Boolean))

  const mine = posts.filter((post) => {
    const row = post as unknown as Record<string, unknown>
    const userData = (row.user_data && typeof row.user_data === 'object')
      ? (row.user_data as Record<string, unknown>)
      : null
    const postCandidates = [
      normalize(row.user_name),
      normalize(userData?.name),
      normalize(userData?.email),
      normalize(row.user_email),
    ].filter(Boolean)
    return postCandidates.some((name) => candidates.has(name))
  })

  // If matching fails due payload shape, keep original list to avoid hiding valid rows.
  return mine.length > 0 ? mine : posts
}

const extractApiMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const record = payload as Record<string, unknown>
  const msg = record.message
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null
}

const normalizeBaseUrl = (value: string | null | undefined) => String(value || '').trim().replace(/\/+$/, '')

const resolveScopedOrgInstanceIdForUserPostList = (authState: ReturnType<typeof useAuthStore.getState>) => {
  const fromCurrentBranchParent = String(authState.currentBranch?.parentInstanceId || '').trim()
  if (fromCurrentBranchParent) {
    return fromCurrentBranchParent
  }

  const fromAvailableBranchParent = (authState.availableBranches || [])
    .map((branch) => String(branch?.parentInstanceId || '').trim())
    .find((value) => Boolean(value))
  if (fromAvailableBranchParent) {
    return fromAvailableBranchParent
  }

  const currentPost = authState.currentPost
  const posts = [currentPost, ...(authState.availablePosts || [])]
  const orgScopePost = posts.find((post) => {
    if (!post) {
      return false
    }
    const variantUniqueId = String(post.service_variant_unique_id ?? '').trim()
    const variantLabel = String(post.service_variant_name || post.service_variant_type || '').trim().toLowerCase()
    return variantUniqueId
      ? isOrganizationServiceVariant(variantUniqueId, null)
      : isOrganizationServiceVariant(null, variantLabel)
  })

  if (orgScopePost) {
    const orgInstanceId = String(orgScopePost.service_instance ?? orgScopePost.service_instance_id ?? '').trim()
    if (orgInstanceId) {
      return orgInstanceId
    }
  }

  return ''
}

const isNetworkOnlyAxiosError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  // Axios sets error.response for server responses; no response means transport/network failure.
  return !error.response
}

const resolveUserPostListFallbackOrder = () => {
  const current = normalizeBaseUrl(getApiBaseUrl())
  if (isApiBaseUrlLocked()) {
    return current ? [current] : []
  }

  const candidates = [current, ...USER_POSTLIST_FALLBACK_BASE_URLS.map(normalizeBaseUrl)]
  const seen = new Set<string>()
  return candidates.filter((candidate) => {
    if (!candidate || seen.has(candidate)) {
      return false
    }
    seen.add(candidate)
    return true
  })
}

const normalizePostKeyPart = (value: unknown) => String(value ?? '').trim().toLowerCase()

const getPostServiceInstanceKey = (post: Post | null | undefined) => normalizePostKeyPart(
  post?.service_instance ?? post?.service_instance_id,
)

const getPostRoleIdentityKey = (post: Post | null | undefined) => {
  const stableRoleId = normalizePostKeyPart(post?.role_id ?? post?.roles_data?.unique_id)
  if (stableRoleId) {
    return stableRoleId
  }

  return [
    normalizePostKeyPart(post?.role_name ?? post?.roles_data?.roles_name),
    normalizePostKeyPart(post?.service_variant_unique_id ?? post?.service_variant_name ?? post?.service_variant_type),
    normalizePostKeyPart(post?.org_name),
    normalizePostKeyPart(post?.service_instance_name),
  ].join('|')
}

const isSamePostIdentity = (left: Post | null | undefined, right: Post | null | undefined) => {
  if (!left || !right) {
    return false
  }

  if (left._id != null && right._id != null && String(left._id) === String(right._id)) {
    return true
  }

  const leftServiceInstance = getPostServiceInstanceKey(left)
  const rightServiceInstance = getPostServiceInstanceKey(right)

  if (String(left.id) === String(right.id) && (!leftServiceInstance || !rightServiceInstance || leftServiceInstance === rightServiceInstance)) {
    return true
  }

  if (!leftServiceInstance || leftServiceInstance !== rightServiceInstance) {
    return false
  }

  const leftRoleIdentity = getPostRoleIdentityKey(left)
  const rightRoleIdentity = getPostRoleIdentityKey(right)
  return Boolean(leftRoleIdentity) && leftRoleIdentity === rightRoleIdentity
}

const mergePostLists = (primary: Post[], secondary: Post[]): Post[] => {
  const merged: Post[] = [...primary]
  for (const candidate of secondary) {
    const exists = merged.some((post) => isSamePostIdentity(post, candidate))
    if (!exists) {
      merged.push(candidate)
    }
  }
  return merged
}

// Deduplicate concurrent fetchPostsList calls
let postsListPromise: Promise<PostsListResult> | null = null
const postsListCapabilityPromises = new Map<string, Promise<PostsListResult>>()

const fetchPostsList = async (options?: PostsListFetchOptions): Promise<PostsListResult> => {
  const includeCapabilities = Boolean(options?.includeCapabilities)
  const forceRefresh = Boolean(options?.forceRefresh)
  const normalizedPostId = options?.postId == null ? '' : String(options.postId).trim()
  const shouldDeduplicate = !includeCapabilities && !normalizedPostId
  const capabilityDedupeKey = includeCapabilities
    ? `caps:${normalizedPostId || 'all'}`
    : ''

  if (shouldDeduplicate && !forceRefresh) {
    const authState = useAuthStore.getState()
    const postsFetchedAt = authState.postsFetchedAt ?? 0
    const hasFreshCachedPosts =
      authState.availablePosts.length > 0
      && postsFetchedAt > 0
      && Date.now() - postsFetchedAt < POSTS_LIST_CACHE_TTL_MS
    if (hasFreshCachedPosts) {
      return {
        posts: authState.availablePosts,
        message: null,
      }
    }
  }

  const runFetch = async () => {
    const authState = useAuthStore.getState()
    const orgInstanceId = resolveScopedOrgInstanceIdForUserPostList(authState)
    const requestConfig = {
      skipPostIdHeader: true,
      skipServiceInstanceId: true,
      params: {
        _ts: Date.now(),
        ...(orgInstanceId ? { org_instance_id: orgInstanceId } : {}),
        ...(includeCapabilities ? { include_capabilities: 1 } : {}),
        ...(normalizedPostId ? { post_id: normalizedPostId } : {}),
      },
    } as any

    const fetchFromBase = async (baseUrl?: string, extraParams?: Record<string, string | number>) => {
      const normalizedBaseUrl = normalizeBaseUrl(baseUrl)
      const requestUrl = normalizedBaseUrl
        ? `${normalizedBaseUrl}${API_PATHS.posts.userList}`
        : API_PATHS.posts.userList
      return api.get<PostsListResponse>(requestUrl, {
        ...requestConfig,
        params: {
          ...requestConfig.params,
          ...extraParams,
        },
      })
    }

    const fetchAllPages = async (baseUrl?: string, scopeParams?: Record<string, string | number>) => {
      const firstResponse = await fetchFromBase(baseUrl, scopeParams)
      if (shouldDebugUserPostList() && typeof window !== 'undefined') {
        const rawPayload = firstResponse.data as unknown
        const rawData = rawPayload && typeof rawPayload === 'object' && 'data' in (rawPayload as Record<string, unknown>)
          ? (rawPayload as { data?: unknown }).data
          : rawPayload
        const rawRows = Array.isArray(rawData)
          ? rawData
          : (rawData && typeof rawData === 'object' && Array.isArray((rawData as { results?: unknown[] }).results)
            ? (rawData as { results: unknown[] }).results
            : [])

        const summary = rawRows.map((entry) => {
          const row = (entry && typeof entry === 'object') ? (entry as Record<string, unknown>) : {}
          const rolesData = (row.roles_data && typeof row.roles_data === 'object')
            ? (row.roles_data as Record<string, unknown>)
            : {}
          return {
            id: row.id ?? row._id ?? null,
            role_name: row.role_name ?? row.roles_name ?? rolesData.roles_name ?? null,
            role_id: row.role_id ?? rolesData.id ?? rolesData.unique_id ?? null,
            service_instance_id: row.service_instance_id ?? row.service_instance ?? null,
            status: row.status ?? null,
            hidden: row.hidden ?? null,
          }
        })

        ;(window as any).__lastUserPostListDebug = {
          requestUrl: baseUrl ? `${baseUrl}${API_PATHS.posts.userList}` : API_PATHS.posts.userList,
          requestParams: {
            ...requestConfig.params,
            ...(scopeParams || {}),
          },
          rawCount: rawRows.length,
          rawSummary: summary,
          rawPayload,
        }
        console.log('[UserPostList debug]', (window as any).__lastUserPostListDebug)
      }

      const merged = new Map<string, Post>()
      for (const post of filterPostsForCurrentUser(extractPostsRows(firstResponse.data))) {
        const key = post.id != null ? String(post.id) : ''
        if (key) merged.set(key, post)
      }

      const payload = firstResponse.data
      const data = payload && typeof payload === 'object' && 'data' in payload
        ? (payload as { data?: unknown }).data
        : payload
      const totalCount = data && typeof data === 'object' && data !== null && 'count' in data
        ? Number((data as { count?: unknown }).count)
        : merged.size
      let nextUrl = data && typeof data === 'object' && data !== null && 'next' in data
        ? String((data as { next?: unknown }).next || '').trim()
        : ''

      let page = 2
      while (nextUrl && merged.size < totalCount && page <= 50) {
        const pageResponse = await fetchFromBase(baseUrl, {
          ...(scopeParams || {}),
          page,
          page_size: POSTS_LIST_PAGE_SIZE,
        })
        const batch = filterPostsForCurrentUser(extractPostsRows(pageResponse.data))
        if (batch.length === 0) break
        for (const post of batch) {
          const key = post.id != null ? String(post.id) : ''
          if (key) merged.set(key, post)
        }

        const pagePayload = pageResponse.data
        const pageData = pagePayload && typeof pagePayload === 'object' && 'data' in pagePayload
          ? (pagePayload as { data?: unknown }).data
          : pagePayload
        nextUrl = pageData && typeof pageData === 'object' && pageData !== null && 'next' in pageData
          ? String((pageData as { next?: unknown }).next || '').trim()
          : ''
        page += 1
      }

      return {
        posts: Array.from(merged.values()),
        message: extractApiMessage(firstResponse.data),
      }
    }

    const resolveScopedServiceInstanceId = () => {
      const value = authState.serviceInstanceId
        || authState.currentPost?.service_instance
        || authState.currentPost?.service_instance_id
        || ''
      return String(value).trim()
    }

    const fetchCombined = async (baseUrl?: string) => {
      const orgScoped = await fetchAllPages(baseUrl)
      const scopedServiceInstanceId = resolveScopedServiceInstanceId()
      if (!scopedServiceInstanceId || scopedServiceInstanceId === orgInstanceId) {
        return orgScoped
      }

      const serviceScoped = await fetchAllPages(baseUrl, {
        service_instance_id: scopedServiceInstanceId,
      })

      return {
        posts: mergePostLists(orgScoped.posts, serviceScoped.posts),
        message: orgScoped.message || serviceScoped.message,
      }
    }

    try {
      const result = await fetchCombined()
      return result
    } catch (error) {
      if (!isNetworkOnlyAxiosError(error)) {
        throw error
      }

      const fallbackBaseUrls = resolveUserPostListFallbackOrder()
      let lastError: unknown = error

      for (const baseUrl of fallbackBaseUrls) {
        try {
          const result = await fetchCombined(baseUrl)
          if (
            !isApiBaseUrlLocked()
            && !isLocalBackendBaseUrl(baseUrl)
            && normalizeBaseUrl(getApiBaseUrl()) !== baseUrl
          ) {
            setApiBaseUrl(baseUrl)
          }
          return result
        } catch (fallbackError) {
          lastError = fallbackError
          if (!isNetworkOnlyAxiosError(fallbackError)) {
            throw fallbackError
          }
        }
      }

      throw lastError
    }
  }

  // If a generic list request is already in flight, reuse it.
  if (shouldDeduplicate && postsListPromise) {
    return postsListPromise
  }

  if (capabilityDedupeKey && !forceRefresh) {
    const inFlight = postsListCapabilityPromises.get(capabilityDedupeKey)
    if (inFlight) {
      return inFlight
    }
  }

  if (!shouldDeduplicate) {
    if (!capabilityDedupeKey) {
      return runFetch()
    }
    const capabilityPromise = runFetch().finally(() => {
      postsListCapabilityPromises.delete(capabilityDedupeKey)
    })
    postsListCapabilityPromises.set(capabilityDedupeKey, capabilityPromise)
    return capabilityPromise
  }

  postsListPromise = runFetch().finally(() => {
    // Always clear after deduped request completion.
    postsListPromise = null
  })

  return postsListPromise
}

// Auth API functions matching the backend spec
export const authApi = {
  login: async (payload: LoginPayload): Promise<LoginResponse> => {
    const loginType = payload.type ?? (payload.phn_nbr ? 'phn_nbr' : payload.username ? 'username' : 'email')
    const fcmToken = payload.fcm_token
      || await requestFcmToken().catch(() => null)
      || (typeof window !== 'undefined' ? window.localStorage.getItem('fcm_token') : null)
    if (fcmToken && typeof window !== 'undefined') {
      window.localStorage.setItem('fcm_token', fcmToken)
    }
    const requestPayload = {
      type: loginType,
      ...(payload.email ? { email: payload.email } : {}),
      ...(payload.username ? { username: payload.username } : {}),
      ...(payload.phn_nbr ? { phn_nbr: payload.phn_nbr } : {}),
      password: payload.password,
      ...(payload.tenant ? { tenant: payload.tenant } : {}),
      ...(payload.company_identifier ? { company_identifier: payload.company_identifier } : {}),
      ...(payload.service_instance_id ? { service_instance_id: payload.service_instance_id } : {}),
      ...(payload.org_instance_id ? { org_instance_id: payload.org_instance_id } : {}),
      user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
      platform: payload.platform ?? DEFAULT_PLATFORM,
      device_id: payload.device_id || DEFAULT_DEVICE_ID,
      ...(fcmToken ? { token: fcmToken } : {}),
    }

    // Never let a previously-stored (possibly stale/deleted) org id from an
    // earlier session ride along on this request -- the backend's global
    // ServiceInstanceStatusGuardMiddleware gives header/query service_instance_id
    // priority over the tenant in the body, so a stale id here 401s the login
    // outright ("Invalid Service_Instance ID") before credentials are even
    // checked, even though `tenant` correctly identifies the org being logged
    // into. Reproduced against logs/api-sql-profile.log: 401s landed in ~60-80ms
    // with 8 queries (middleware short-circuit) vs ~900ms+/33-46 queries for a
    // real login.
    const response = await api.post<LoginResponse>(API_PATHS.auth.login, requestPayload, {
      skipServiceInstanceId: true,
    } as any)
    const data = response.data

    const normalizedStatus = data.status?.toLowerCase()
    const hasSuccessfulLoginPayload = Boolean(
      data.data?.access_token && data.data?.refresh_token && data.data?.user
    )
    const hasFollowupContext = Boolean(data.data?.context)

    if (!hasSuccessfulLoginPayload && !hasFollowupContext && normalizedStatus === 'failed') {
      throw new Error(data.message || 'Login failed')
    }

    if (!hasSuccessfulLoginPayload && !hasFollowupContext && !normalizedStatus) {
      throw new Error(data.message || 'Login failed')
    }

    const accessToken = data.data.access_token
    const refreshToken = data.data.refresh_token
    const authenticatedUser = data.data.user

    if (accessToken && refreshToken && authenticatedUser) {
      const resolvedServiceInstanceId = data.data.service_instance_id
        || requestPayload.service_instance_id
        || requestPayload.org_instance_id
      // Map API response to our User type
      const user: User = {
        id: authenticatedUser.id,
        email: authenticatedUser.email,
        first_name: authenticatedUser.fname || '',
        last_name: authenticatedUser.lname || '',
        username: authenticatedUser.username,
        status: (authenticatedUser as Record<string, unknown>).status as string | number | null | undefined,
        registration_status: (authenticatedUser as Record<string, unknown>).registration_status as string | number | null | undefined,
      }
      
      // Store tokens and user in state
      useAuthStore.getState().login(
        user,
        accessToken,
        refreshToken,
        data.data.posts || [],
        data.data.session_id,
        resolvedServiceInstanceId
      )
    }

    return data
  },

  signup: async (payload: SignupPayload): Promise<SignupResponse> => {
    const requestPayload = {
      signup_type: payload.signup_type,
      ...(payload.signup_type === 'company'
        ? {
            company_name: payload.company_name,
            identifier: payload.identifier,
            facility_product: payload.facility_product,
            owner_name: payload.owner_name,
            org_instance_id: payload.org_instance_id,
            country: payload.country_id,
            phone_number: payload.phone_number,
          }
        : payload.signup_type === 'vendor'
          ? {
              vendor_name: payload.vendor_name,
              branch_name: payload.branch_name,
              service_variant_id: payload.service_variant_id,
              org_instance_id: payload.org_instance_id,
              country: payload.country_id,
              phone_number: payload.phone_number,
              accept_user_policy: payload.accept_user_policy,
            }
          : {
              fname: payload.first_name || payload.name,
              lname: payload.last_name,
              phn_nbr: payload.phone_number,
              country_id: payload.country_id,
              state_id: payload.state_id,
              dob: payload.date_of_birth,
              age: payload.age,
              gender: payload.gender,
              tenant: payload.tenant,
              company_identifier: payload.company_identifier,
              service_instance_id: payload.service_instance_id,
              org_instance_id: payload.org_instance_id,
              acknowledge_exposure_notice: payload.acknowledge_exposure_notice,
          }),
      ...(payload.email ? { email: payload.email } : {}),
      password: payload.password,
      user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
    }
    
    // See the matching comment on `login` above -- a stale stored org id must
    // never override (or get merged with) the org this signup is explicitly
    // targeting via `org_instance_id`/`facility_product` in the body.
    const response = await api.post<SignupResponse>(API_PATHS.auth.signup, requestPayload, {
      skipServiceInstanceId: true,
    } as any)
    return response.data
  },

  securityCompanySignup: async (
    payload: SecurityCompanySignupPayload,
  ): Promise<SecurityCompanySignupResponse> => {
    const response = await api.post<SecurityCompanySignupResponse>(
      API_PATHS.security.companySignup,
      payload,
      { skipServiceInstanceId: true } as any,
    )
    return response.data
  },

  logout: async (redirectPath?: string): Promise<void> => {
    const authState = useAuthStore.getState()
    const redirectServiceInstanceId = authState.serviceInstanceId

    void api.patch(
      API_PATHS.auth.logout,
      {
        platform: DEFAULT_PLATFORM,
        device_id: DEFAULT_DEVICE_ID,
      },
      {
        skipAuthRedirectOn401: true,
        skipPostIdHeader: true,
      } as any,
    ).catch(() => undefined)

    authState.logout()

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace(redirectPath || buildLoginRedirectPath(redirectServiceInstanceId ?? undefined))
    }
  },

  logoutApiOnly: async (): Promise<void> => {
    await api.patch(
      API_PATHS.auth.logout,
      {
        platform: DEFAULT_PLATFORM,
        device_id: DEFAULT_DEVICE_ID,
      },
      {
        skipAuthRedirectOn401: true,
        skipPostIdHeader: true,
      } as any,
    ).catch(() => undefined)
  },

  refreshAuthState: async (): Promise<Post[]> => {
    const authState = useAuthStore.getState()

    if (!authState.accessToken || !authState.serviceInstanceId) {
      return authState.availablePosts
    }

    const response = await api.get<AuthContextResponse>(
      API_PATHS.auth.getByToken,
      { skipPostIdHeader: true } as any
    )
    const posts = response.data?.data?.posts || []
    const refreshedSessionId = response.data?.data?.session_id
    if (refreshedSessionId) {
      useAuthStore.getState().setSessionId(refreshedSessionId)
    }
    const existing = useAuthStore.getState().availablePosts

    // Don't wipe existing posts if the API returns empty — keep what's stored
    if (posts.length > 0 || existing.length === 0) {
      useAuthStore.getState().setPosts(posts)
    }

    return posts.length > 0 ? posts : existing
  },

  listPostsDirect: async (): Promise<Post[]> => {
    const { posts } = await fetchPostsList()

    if (posts.length > 0) {
      useAuthStore.getState().setPosts(posts)
    }

    return posts
  },

  hydrateSelectedRoleCapabilities: async (selectedPost?: string | number | Post | null): Promise<void> => {
    const authState = useAuthStore.getState()
    const targetPost = selectedPost && typeof selectedPost === 'object'
      ? selectedPost
      : authState.currentPost
    const targetPostId = selectedPost && typeof selectedPost !== 'object'
      ? selectedPost
      : targetPost?.id
    const targetPostKey = getBookmarkedPostKey(targetPost)
    if (targetPostId == null) {
      return
    }

    // Already hydrated for this role — skip UserPostList on in-app / focus churn.
    if (targetPost && hasCapabilityLikePayload(targetPost.capabilities)) {
      return
    }

    const isTargetPost = (post: Post | null | undefined) => {
      if (!post) {
        return false
      }

      const postKey = getBookmarkedPostKey(post)
      if (targetPostKey && postKey && postKey === targetPostKey) {
        return true
      }

      return targetPost != null && isSamePostIdentity(post, targetPost)
    }

    const mergeHydratedPost = (hydratedPost: Post) => {
      const latestState = useAuthStore.getState()
      const mergedPosts = latestState.availablePosts.map((post) => {
        if (!isTargetPost(post)) {
          return post
        }

        return {
          ...post,
          ...hydratedPost,
          id: post.id ?? hydratedPost.id,
          _id: post._id ?? hydratedPost._id,
          roles_data: {
            ...(post.roles_data ?? {}),
            ...(hydratedPost.roles_data ?? {}),
          },
          capabilities: hydratedPost.capabilities ?? post.capabilities,
        }
      })

      const store = useAuthStore.getState()
      store.setPosts(mergedPosts)

      if (store.currentPost && isTargetPost(store.currentPost)) {
        store.setPost({
          ...store.currentPost,
          ...hydratedPost,
          id: store.currentPost.id ?? hydratedPost.id,
          _id: store.currentPost._id ?? hydratedPost._id,
          roles_data: {
            ...(store.currentPost.roles_data ?? {}),
            ...(hydratedPost.roles_data ?? {}),
          },
          capabilities: hydratedPost.capabilities ?? store.currentPost.capabilities,
        })
      }
    }

    // First try a targeted hydration for the selected role only.
    try {
      const targeted = await fetchPostsList({ includeCapabilities: true, postId: targetPostId })
      const selectedHydratedPost = targeted.posts.find((post) => isTargetPost(post))
        ?? (!targetPostKey && targeted.posts.length === 1 ? targeted.posts[0] : null)

      if (selectedHydratedPost) {
        mergeHydratedPost(selectedHydratedPost)
        return
      }

      // Targeted call succeeded but did not include this role — only fall back
      // when the selected role still has no capabilities in the local store.
      const latestTarget = useAuthStore.getState().availablePosts.find((post) => isTargetPost(post))
        ?? (isTargetPost(useAuthStore.getState().currentPost) ? useAuthStore.getState().currentPost : null)
      if (latestTarget && hasCapabilityLikePayload(latestTarget.capabilities)) {
        return
      }
    } catch {
      // Fall through to broad hydration.
    }

    // Fallback: hydrate full post list so guards are never blocked by missing capability data.
    const fallback = await fetchPostsList({ includeCapabilities: true })
    if (fallback.posts.length > 0) {
      const store = useAuthStore.getState()
      store.setPosts(fallback.posts)

      const hydratedCurrentPost = fallback.posts.find((post) => isTargetPost(post))
      if (hydratedCurrentPost) {
        store.setPost({
          ...hydratedCurrentPost,
          id: store.currentPost && isTargetPost(store.currentPost)
            ? (store.currentPost.id ?? hydratedCurrentPost.id)
            : hydratedCurrentPost.id,
          _id: store.currentPost && isTargetPost(store.currentPost)
            ? (store.currentPost._id ?? hydratedCurrentPost._id)
            : hydratedCurrentPost._id,
        })
      }
    }
  },

  listPostsDirectWithMessage: async (): Promise<PostsListResult> => {
    const result = await fetchPostsList({ forceRefresh: true })

    if (result.posts.length > 0) {
      useAuthStore.getState().setPosts(result.posts)
    }

    return result
  },

  listPosts: async (_serviceInstanceIdOverride?: string | number | null): Promise<Post[]> => {
    const { posts } = await fetchPostsList()

    if (posts.length === 0) {
      const authState = useAuthStore.getState()

      if (authState.accessToken && authState.sessionId && authState.serviceInstanceId) {
        return authApi.refreshAuthState()
      }

      // No credentials to refresh — return existing posts without clearing them
      return authState.availablePosts
    }

    useAuthStore.getState().setPosts(posts)

    return posts
  },

  listPostsWithMessage: async (_serviceInstanceIdOverride?: string | number | null): Promise<PostsListResult> => {
    const result = await fetchPostsList({ forceRefresh: true })
    const posts = result.posts

    if (posts.length === 0) {
      const authState = useAuthStore.getState()

      if (authState.accessToken && authState.sessionId && authState.serviceInstanceId) {
        const refreshed = await authApi.refreshAuthState()
        return {
          posts: refreshed,
          message: result.message,
        }
      }

      // No credentials to refresh — return existing posts without clearing them
      return {
        posts: authState.availablePosts,
        message: result.message,
      }
    }

    useAuthStore.getState().setPosts(posts)

    return result
  },

  forgotPassword: async (payload: ForgotPasswordPayload): Promise<ApiResponse<ForgotPasswordResponseData>> => {
    const requestPayload = {
      org_instance_id: payload.org_instance_id || DEFAULT_SERVICE_INSTANCE_ID,
      ...(payload.email ? { email: payload.email } : {}),
      ...(payload.phn_nbr ? { phn_nbr: payload.phn_nbr } : {}),
      ...(payload.country_id != null ? { country_id: payload.country_id } : {}),
      user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
    }
    
    const response = await api.post<ApiResponse<ForgotPasswordResponseData>>(API_PATHS.auth.forgotPassword, requestPayload)
    return response.data
  },

  resetPassword: async (payload: ResetPasswordPayload): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(API_PATHS.auth.resetPassword, {
      ...payload,
      user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
    })
    return response.data
  },

  changePassword: async (payload: ChangePasswordPayload): Promise<ApiResponse> => {
    const response = await api.patch<ApiResponse>(API_PATHS.auth.changePassword, {
      old_pwd: payload.old_pwd,
      new_pwd: payload.new_pwd,
    })
    return response.data
  },

  verifySignupEmailOtp: async (payload: VerifySignupEmailOtpPayload): Promise<ApiResponse<{ context?: string; token?: string }>> => {
    const response = await api.post<ApiResponse<{ context?: string; token?: string }>>(
      API_PATHS.auth.verifyEmailOtp,
      {
        ...payload,
        user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
      },
    )
    return response.data
  },

  verifyForgotPasswordEmailOtp: async (payload: VerifyForgotPasswordEmailOtpPayload): Promise<ApiResponse<{ context?: string; token?: string }>> => {
    const response = await api.post<ApiResponse<{ context?: string; token?: string }>>(
      API_PATHS.auth.verifyEmailOtp,
      {
        ...payload,
        user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
      },
    )
    return response.data
  },

  verifyForgotPasswordPhoneOtp: async (payload: VerifyForgotPasswordPhoneOtpPayload): Promise<ApiResponse<{ context?: string; token?: string }>> => {
    const response = await api.post<ApiResponse<{ context?: string; token?: string }>>(
      API_PATHS.auth.verifyPhoneOtp,
      {
        ...payload,
        user_group_unique: payload.user_group_unique || DEFAULT_USER_GROUP_UNIQUE,
      },
    )
    return response.data
  },

  resendSignupVerificationEmail: async (
    payload: ResendSignupVerificationEmailPayload,
  ): Promise<ApiResponse<{ message?: string; otp_length?: number }>> => {
    const response = await api.post<ApiResponse<{ message?: string; otp_length?: number }>>(
      API_PATHS.auth.resendVerificationEmail,
      payload,
    )
    return response.data
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get<ApiResponse<User>>(API_PATHS.profile.get)
    return response.data.data
  },

  // Update user profile
  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.patch<ApiResponse<User>>(API_PATHS.profile.update, data)
    return response.data.data
  },

  // Get service branches
  getServiceInstances: async (): Promise<Branch[]> => {
    const response = await api.get<ApiResponse<ServiceInstanceRecord[] | PaginatedResponse<ServiceInstanceRecord>> | PaginatedResponse<ServiceInstanceRecord> | ServiceInstanceRecord[]>(
      API_PATHS.organisation.branches.list,
      { skipPostIdHeader: true, skipAuthRedirectOn401: true } as any,
    )
    return normalizeBranches(response.data)
  },
}

export default authApi
